'use client';

import { ChevronRight, type LucideIcon } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '../utils/cn';

export type DetailListRowProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'value'
> & {
  icon?: LucideIcon;
  label: string;
  /** Right-aligned value (text or custom node). */
  value?: ReactNode;
  /** Show trailing chevron (default true when interactive). */
  chevron?: boolean;
  /** Highlight the row (e.g. active field while sheet is open). */
  highlighted?: boolean;
  /** Render as a non-button static row. */
  nonInteractive?: boolean;
  className?: string;
};

/**
 * Compact tappable property row: icon · label · value · chevron.
 * Used by mobile lead detail (“ein Feld, ein Sheet”) and similar list sheets.
 */
export function DetailListRow({
  icon: Icon,
  label,
  value,
  chevron,
  highlighted = false,
  nonInteractive = false,
  className,
  type = 'button',
  disabled,
  onClick,
  ...props
}: DetailListRowProps) {
  const showChevron = chevron ?? !nonInteractive;
  const rowClass = cn(
    'flex w-full items-center gap-3 border-t border-[color:var(--surface-border)] px-3.5 text-left first:border-t-0',
    'min-h-12',
    highlighted && 'bg-[color:var(--surface-2)]',
    disabled && 'opacity-60',
    className,
  );

  const content = (
    <>
      {Icon ? (
        <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      ) : null}
      <span className="w-[92px] shrink-0 text-[13px] text-muted-foreground">
        {label}
      </span>
      <span className="flex min-w-0 flex-1 items-center justify-end gap-1.5 overflow-hidden text-[13px] text-foreground">
        {typeof value === 'string' || typeof value === 'number' ? (
          // Text truncates at its end: in this right-aligned, clipped row a
          // bare long value lost its start ("…scutting1@example.com", DEV-560).
          <span className="min-w-0 truncate">{value}</span>
        ) : (
          (value ?? <span className="text-muted-foreground/70">—</span>)
        )}
      </span>
      {showChevron ? (
        <ChevronRight
          className="size-[15px] shrink-0 text-muted-foreground"
          aria-hidden
        />
      ) : null}
    </>
  );

  if (nonInteractive) {
    return <div className={rowClass}>{content}</div>;
  }

  return (
    <button
      type={type}
      disabled={disabled}
      className={rowClass}
      onClick={onClick}
      {...props}
    >
      {content}
    </button>
  );
}

export type DetailListCardProps = {
  children: ReactNode;
  className?: string;
};

/** Grouped card shell for {@link DetailListRow} stacks. */
export function DetailListCard({ children, className }: DetailListCardProps) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-[var(--radius-card-mobile,16px)] border border-[color:var(--surface-border)] bg-card shadow-[var(--shadow-card)]',
        className,
      )}
    >
      <div className="-mt-px">{children}</div>
    </div>
  );
}
